/**
 * Endorsements API (P4.1) — manager acknowledgement with provenance.
 *
 * RTK Query endpoints for /endorsements. The server records the endorser's
 * role and workspace from Central; nothing the client sends is an identity
 * except the learner the evidence must belong to.
 */

import { api } from '../api';

import type {
  Endorsement,
  CreateEndorsementRequest,
} from '../../components/endorsements/endorsementTypes';

export type {
  Endorsement,
  EndorsementEvidenceKind,
  PublicEndorsement,
  CreateEndorsementRequest,
} from '../../components/endorsements/endorsementTypes';
export { ENDORSEMENT_STATEMENT_MAX, EVIDENCE_KIND_LABELS } from '../../components/endorsements/endorsementTypes';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const endorsementsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMyEndorsements: builder.query<Endorsement[], void>({
      query: () => '/endorsements/me',
      transformResponse: (r: ApiEnvelope<Endorsement[]>) => r.data,
      providesTags: ['Endorsements'],
    }),

    getGivenEndorsements: builder.query<Endorsement[], void>({
      query: () => '/endorsements/given',
      transformResponse: (r: ApiEnvelope<Endorsement[]>) => r.data,
      providesTags: ['Endorsements'],
    }),

    createEndorsement: builder.mutation<Endorsement, CreateEndorsementRequest>({
      query: (body) => ({ url: '/endorsements', method: 'POST', body }),
      transformResponse: (r: ApiEnvelope<Endorsement>) => r.data,
      invalidatesTags: ['Endorsements'],
    }),

    revokeEndorsement: builder.mutation<Endorsement, { id: string; reason: string }>({
      query: ({ id, reason }) => ({ url: `/endorsements/${id}/revoke`, method: 'POST', body: { reason } }),
      transformResponse: (r: ApiEnvelope<Endorsement>) => r.data,
      invalidatesTags: ['Endorsements', 'PublicProfile'],
    }),

    setEndorsementVisibility: builder.mutation<Endorsement, { id: string; visibleOnPortfolio: boolean }>({
      query: ({ id, visibleOnPortfolio }) => ({
        url: `/endorsements/${id}/visibility`,
        method: 'PATCH',
        body: { visibleOnPortfolio },
      }),
      transformResponse: (r: ApiEnvelope<Endorsement>) => r.data,
      invalidatesTags: ['Endorsements', 'PublicProfile'],
    }),
  }),
});

export const {
  useGetMyEndorsementsQuery,
  useGetGivenEndorsementsQuery,
  useCreateEndorsementMutation,
  useRevokeEndorsementMutation,
  useSetEndorsementVisibilityMutation,
} = endorsementsApi;
